import React, { useEffect, useRef, useState } from "react";
import { Avatar, Badge, Button, Card, Col, Empty, Input, List, Modal, Row, Space, Spin, Tag, Tooltip, Typography } from "antd";
import { AudioMutedOutlined, AudioOutlined, DeleteOutlined, FileTextOutlined, LeftCircleOutlined, RightCircleOutlined, SearchOutlined, ShoppingCartOutlined, ShoppingOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router";
import ChatImagePicker from "components/pickers/image/ChatImagePicker";
import { formatCurrency } from "utils/number";

const URL_PATTERN = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;

const normalizeURL = (value = "") => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
};

const extractURLs = (text = "") => {
  const matches = String(text || "").match(URL_PATTERN) || [];
  const urls = matches.map((item) => normalizeURL(item)).filter(Boolean);
  return [...new Set(urls)];
};

const renderTextWithLinks = (text = "") => {
  const source = String(text || "");
  const parts = source.split(URL_PATTERN);
  return parts.map((part, index) => {
    if (!part) return null;
    const isLink = URL_PATTERN.test(part);
    URL_PATTERN.lastIndex = 0;
    if (!isLink) return <React.Fragment key={`${index}-${part}`}>{part}</React.Fragment>;
    const href = normalizeURL(part);
    return <a key={`${index}-${href}`} href={href} target="_blank" rel="noreferrer" style={{ color: "inherit", textDecoration: "underline" }}>{part}</a>;
  });
};

const getInitials = (text = "") => {
  const words = String(text || "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return "KH";
  }
  return words.slice(0, 2).map((item) => item.charAt(0).toUpperCase()).join("");
};

const formatTime = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("vi-VN", {
    hour: "2-digit",
    minute: "2-digit",
    day: "2-digit",
    month: "2-digit",
  }).format(date);
};

const formatTimeGroup = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("vi-VN", {
    hour: "2-digit",
    minute: "2-digit",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

const shouldShowTimeGroup = (list = [], index = 0) => {
  if (index <= 0) {
    return true;
  }

  const current = new Date(list[index]?.create_on || 0).getTime();
  const previous = new Date(list[index - 1]?.create_on || 0).getTime();
  if (!current || !previous) {
    return false;
  }

  return Math.abs(current - previous) >= 15 * 60 * 1000;
};

const getImageAttachments = (attachments = []) =>
  (attachments || []).filter((item) => item?.type === "image" && item?.url);

const getNonImageAttachments = (attachments = []) =>
  (attachments || []).filter((item) => !(item?.type === "image" && item?.url));

const resolveShippingAddress = (meta = {}) => {
  if (!meta || typeof meta !== "object") {
    return "-";
  }

  if (typeof meta.shipping_address === "string") {
    return String(meta.shipping_address || "").trim() || "-";
  }

  const value = meta?.shipping_address?.address || meta?.shipping_address?.full_address || meta?.customer_address || meta?.address || "";
  return String(value || "").trim() || "-";
};

const renderAttachment = (att, { onPreviewImage, onOpenOrderDetail } = {}) => {
  if (!att) {
    return null;
  }

  if (att.type === "image" && att.url) {
    return (
      <div style={{ marginTop: 8 }}>
        <img
          src={att.url}
          alt={att.title || "chat-image"}
          style={{ maxWidth: 280, width: "100%", maxHeight: 260, objectFit: "cover", borderRadius: 12, border: "1px solid rgba(148,163,184,0.25)", cursor: "zoom-in" }}
          onClick={() => onPreviewImage?.(att.url)}
        />
      </div>
    );
  }

  if (att.type === "product") {
    return (
      <div style={{ marginTop: 8, borderRadius: 12, background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.3)", padding: 10 }}>
        {!!att.url && (
          <img
            src={att.url}
            alt={att.title || "product"}
            style={{ width: "100%", maxHeight: 140, objectFit: "cover", borderRadius: 10, marginBottom: 8 }}
          />
        )}
        <div style={{ fontWeight: 700, marginBottom: 4 }}><ShoppingOutlined /></div>
        <div>{att.title || att?.meta?.product_name || "Sản phẩm được chia sẻ"}</div>
      </div>
    );
  }

  if (att.type === "order" || att.type === "invoice") {
    const isInvoice = att.type === "invoice";
    const orderID = String(att?.meta?.order_id || "").trim();
    const totalItem = Number(att?.meta?.total_item || 0) || 0;
    const totalAmount = Number(att?.meta?.total_amount || 0) || 0;
    const shippingAddress = resolveShippingAddress(att?.meta || {});
    return (
      <div style={{ marginTop: 8, borderRadius: 12, background: isInvoice ? "rgba(37,99,235,0.1)" : "rgba(249,115,22,0.1)", border: `1px solid ${isInvoice ? "rgba(37,99,235,0.35)" : "rgba(249,115,22,0.35)"}`, padding: 10 }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>{isInvoice ? <FileTextOutlined /> : <ShoppingCartOutlined />}</div>
        <div>{att.title || `#${orderID}`}</div>
        {!!att?.meta?.customer_name && <div style={{ marginTop: 4, fontSize: 12, opacity: 0.85 }}>Khách: {att.meta.customer_name}</div>}
        {isInvoice && (
          <>
            <div style={{ marginTop: 4, fontSize: 12, opacity: 0.9 }}>Tổng sản phẩm: {totalItem}</div>
            <div style={{ marginTop: 2, fontSize: 12, opacity: 0.9 }}>Tổng số tiền: {formatCurrency(totalAmount)}</div>
            <div style={{ marginTop: 2, fontSize: 12, opacity: 0.9 }}>Địa chỉ giao hàng: {shippingAddress}</div>
            <div style={{ marginTop: 8, display: "flex", justifyContent: "flex-end" }}>
              <Button
                size="small"
                onClick={() => onOpenOrderDetail?.(orderID)}
                style={{ borderRadius: 999 }}
                disabled={!orderID}
              >
                Xem
              </Button>
            </div>
          </>
        )}
      </div>
    );
  }

  if (att.type === "cart") {
    const totalItem = Number(att?.meta?.total_item || 0) || 0;
    const totalAmount = Number(att?.meta?.total_amount || 0) || 0;
    return (
      <div style={{ marginTop: 8, borderRadius: 12, background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.28)", padding: 10 }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}><ShoppingCartOutlined /></div>
        <div>{att.title || "Giỏ hàng hiện tại"}</div>
        {!!att?.meta?.customer_name && <div style={{ marginTop: 4, fontSize: 12, opacity: 0.85 }}>Khách: {att.meta.customer_name}</div>}
        <div style={{ marginTop: 4, fontSize: 12, opacity: 0.9 }}>Số sản phẩm: {totalItem}</div>
        <div style={{ marginTop: 2, fontSize: 12, opacity: 0.9 }}>Tổng số tiền: {formatCurrency(totalAmount)}</div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 8, fontSize: 12, opacity: 0.9 }}>
      [{att.type}] {att.title || att.url || "Đính kèm"}
    </div>
  );
};

const ChatDesktop = ({ viewModel }) => {
  const navigate = useNavigate();
  const {
    loading,
    messageLoading,
    sending,
    threadOptions,
    selectedThread,
    messages,
    content,
    keyword,
    setKeyword,
    setContent,
    onSearch,
    onReload,
    onSelectThread,
    onSend,
    linkPreviews,
    attachments,
    typingText,
    chatImagePickerValue,
    onChangeChatImagePicker,
    onRemoveAttachment,
    attachmentPickerOpen,
    attachmentPickerType,
    attachmentPickerKeyword,
    attachmentPickerLoading,
    attachmentPickerOptions,
    setAttachmentPickerKeyword,
    openAttachmentPicker,
    closeAttachmentPicker,
    loadAttachmentPickerData,
    onPickAttachment,
    soundEnabled,
    toggleSoundEnabled,
  } = viewModel;
  const messageListRef = useRef(null);
  const messageEndRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const [previewImages, setPreviewImages] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  const lastStaffMessage = [...messages].reverse().find((item) => item?.sender_type === "shop_staff");

  const previewImage = previewImages[previewIndex] || "";
  const openImagePreview = (images = [], index = 0) => {
    if (!Array.isArray(images) || images.length === 0) {
      return;
    }
    setPreviewImages(images);
    setPreviewIndex(index);
  };

  const closeImagePreview = () => {
    setPreviewImages([]);
    setPreviewIndex(0);
  };

  const showPrevImage = () => {
    if (previewImages.length <= 1) {
      return;
    }
    setPreviewIndex((prev) => (prev - 1 + previewImages.length) % previewImages.length);
  };

  const showNextImage = () => {
    if (previewImages.length <= 1) {
      return;
    }
    setPreviewIndex((prev) => (prev + 1) % previewImages.length);
  };

  const onOpenOrderDetail = (orderID) => {
    const id = String(orderID || "").trim();
    if (!id) {
      return;
    }
    navigate(`/order/${id}`);
  };

  useEffect(() => {
    const container = messageListRef.current;
    if (!container) {
      return;
    }

    const onScroll = () => {
      const threshold = 80;
      const distanceToBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      isNearBottomRef.current = distanceToBottom <= threshold;
    };

    onScroll();
    container.addEventListener("scroll", onScroll);

    return () => {
      container.removeEventListener("scroll", onScroll);
    };
  }, [selectedThread?.id]);

  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: "auto", block: "end" });
  }, [selectedThread?.id]);

  useEffect(() => {
    if (!isNearBottomRef.current) {
      return;
    }
    messageEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length]);

  return (
    <div style={{ minHeight: "calc(100vh - 160px)", background: "linear-gradient(180deg, #f5f7fb 0%, #eef3f9 100%)", borderRadius: 24, padding: 16 }}>
      <Row gutter={16} style={{ minHeight: "calc(100vh - 192px)" }}>
        <Col span={8}>
          <Card
            styles={{ body: { padding: 14, display: "flex", flexDirection: "column", height: "calc(100vh - 224px)" } }}
            style={{ borderRadius: 24, boxShadow: "0 18px 48px rgba(15, 23, 42, 0.08)" }}
            title={<Typography.Text strong style={{ fontSize: 18 }}>Hộp thư khách hàng</Typography.Text>}
            extra={<Button size="small" onClick={onReload}>Tải lại</Button>}
          >
            <Space style={{ width: "100%", marginBottom: 14 }}>
            <Input
              placeholder="Tìm theo tên/số điện thoại"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onPressEnter={onSearch}
              size="large"
              style={{ borderRadius: 14 }}
            />
            <Tooltip title="Tìm">
              <Button onClick={onSearch} size="large" style={{ borderRadius: 14 }} icon={<SearchOutlined />} />
            </Tooltip>
            </Space>

            <div style={{ flex: 1, overflowY: "auto", paddingRight: 4 }}>
              <List
                loading={loading}
                dataSource={threadOptions}
                locale={{ emptyText: <Empty description="Chưa có hội thoại" /> }}
                renderItem={(item) => {
                  const active = selectedThread?.id === item.id;
                  return (
                    <List.Item
                      onClick={() => onSelectThread(item)}
                      style={{
                        cursor: "pointer",
                        background: active ? "linear-gradient(135deg, #e8f1ff 0%, #f3f7ff 100%)" : "#fff",
                        borderRadius: 20,
                        padding: 14,
                        marginBottom: 10,
                        border: active ? "1px solid #b7d0ff" : "1px solid #eef2f7",
                        boxShadow: active ? "0 14px 32px rgba(22, 119, 255, 0.12)" : "0 10px 24px rgba(15, 23, 42, 0.04)",
                      }}
                    >
                      <List.Item.Meta
                        avatar={<Avatar style={{ backgroundColor: active ? "#1677ff" : "#cbd5e1", verticalAlign: "middle" }}>{getInitials(item.customer_name || item.customer_phone || "Khách hàng")}</Avatar>}
                        title={(
                          <Space size={8} style={{ width: "100%", justifyContent: "space-between" }}>
                            <Typography.Text strong>{item.customer_name || item.customer_phone || "Khách hàng"}</Typography.Text>
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>{formatTime(item.last_message_time)}</Typography.Text>
                          </Space>
                        )}
                        description={
                          <Typography.Paragraph ellipsis={{ rows: 2 }} style={{ marginBottom: 0, color: "#64748b" }}>
                            {item.last_message || "Chưa có tin nhắn"}
                          </Typography.Paragraph>
                        }
                      />
                      {!!item.unread_shop && <Badge count={item.unread_shop} color="#1677ff" />}
                    </List.Item>
                  );
                }}
              />
            </div>
          </Card>
        </Col>

        <Col span={16}>
          <Card
            styles={{ body: { padding: 0, display: "flex", flexDirection: "column", height: "calc(100vh - 224px)" } }}
            style={{ borderRadius: 24, overflow: "hidden", boxShadow: "0 18px 48px rgba(15, 23, 42, 0.08)" }}
          >
            {selectedThread ? (
              <>
                <div style={{ padding: "16px 18px", borderBottom: "1px solid #eef2f7", background: "rgba(255,255,255,0.92)", backdropFilter: "blur(10px)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Space size={12}>
                    <Avatar size={48} style={{ backgroundColor: "#1677ff" }}>{getInitials(selectedThread.customer_name || selectedThread.customer_phone || "Khách hàng")}</Avatar>
                    <div>
                      <Typography.Text strong style={{ display: "block", fontSize: 16 }}>{selectedThread.customer_name || selectedThread.customer_phone || "Khách hàng"}</Typography.Text>
                      <Typography.Text type="secondary">{selectedThread.customer_phone || "Khách đang hoạt động trong inbox"}</Typography.Text>
                    </div>
                  </Space>
                  <Tag color="blue" style={{ borderRadius: 999, paddingInline: 12 }}>Messenger style</Tag>
                </div>

                <div ref={messageListRef} style={{ flex: 1, overflowY: "auto", padding: 18, background: "linear-gradient(180deg, #f6f8fc 0%, #edf2f7 100%)" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    <div style={{ display: "flex", justifyContent: "center" }}>
                      <div style={{ background: "#fff", borderRadius: 20, padding: "12px 18px", textAlign: "center", boxShadow: "0 12px 32px rgba(15, 23, 42, 0.06)" }}>
                        <Avatar size={56} style={{ backgroundColor: "#1677ff", marginBottom: 8 }}>{getInitials(selectedThread.customer_name || selectedThread.customer_phone || "Khách hàng")}</Avatar>
                        <Typography.Text strong style={{ display: "block" }}>{selectedThread.customer_name || selectedThread.customer_phone || "Khách hàng"}</Typography.Text>
                        <Typography.Text type="secondary">Hội thoại khách hàng</Typography.Text>
                      </div>
                    </div>

                    {messageLoading ? (
                      <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
                        <Spin tip="Đang tải tin nhắn..." />
                      </div>
                    ) : (
                      <>
                        {messages.length === 0 && <Empty description="Chưa có tin nhắn" image={Empty.PRESENTED_IMAGE_SIMPLE} />}

                        {messages.map((msg, index) => {
                      const isMine = msg.sender_type === "shop_staff";
                      const senderLabel = isMine ? "Bạn" : msg.sender_name || selectedThread.customer_name || "Khách hàng";
                      const isAI = msg.sender_type === "ai_bot";
                      const showSeen = isMine && msg.id === lastStaffMessage?.id && Number(selectedThread?.unread_customer || 0) === 0;
                      const imageAttachments = getImageAttachments(msg.attachments || []);
                      const nonImageAttachments = getNonImageAttachments(msg.attachments || []);
                      return (
                        <React.Fragment key={msg.id}>
                          {shouldShowTimeGroup(messages, index) && (
                            <div style={{ display: "flex", justifyContent: "center" }}>
                              <Typography.Text type="secondary" style={{ fontSize: 12, background: "#fff", borderRadius: 999, padding: "3px 10px", boxShadow: "0 8px 20px rgba(15, 23, 42, 0.08)" }}>
                                {formatTimeGroup(msg.create_on)}
                              </Typography.Text>
                            </div>
                          )}

                          <div style={{ display: "flex", justifyContent: isMine ? "flex-end" : "flex-start" }}>
                            <div style={{ display: "flex", flexDirection: isMine ? "row-reverse" : "row", alignItems: "flex-end", gap: 10, maxWidth: "82%" }}>
                              {!isMine && <Avatar style={{ backgroundColor: "#cbd5e1", flexShrink: 0 }}>{getInitials(senderLabel)}</Avatar>}
                              <div>
                                <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4, textAlign: isMine ? "right" : "left" }}>
                                  {isAI && <Tag color="green" style={{ marginRight: 4, fontSize: 11, padding: "0 5px", lineHeight: "18px", borderRadius: 99 }}>AI</Tag>}
                                  {senderLabel} {formatTime(msg.create_on)}
                                </div>
                                <div style={{ padding: "12px 16px", borderRadius: isMine ? "20px 20px 8px 20px" : "20px 20px 20px 8px", background: isAI ? "#f0fdf4" : isMine ? "#1677ff" : "#fff", color: isAI ? "#166534" : isMine ? "#fff" : "#1f2937", boxShadow: "0 12px 30px rgba(15, 23, 42, 0.08)", border: isAI ? "1px solid #bbf7d0" : undefined }}>
                                  {msg.content && <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{renderTextWithLinks(msg.content)}</div>}
                                  {extractURLs(msg.content || "").map((url) => {
                                    const preview = linkPreviews?.[url];
                                    if (!preview) return null;
                                    return (
                                      <a key={`preview-${msg.id}-${url}`} href={preview.url || url} target="_blank" rel="noreferrer" style={{ textDecoration: "none", color: "inherit" }}>
                                        <div style={{ marginTop: 10, borderRadius: 12, background: isMine ? "rgba(255,255,255,0.2)" : "#f8fafc", border: "1px solid rgba(148,163,184,0.28)", overflow: "hidden" }}>
                                          {preview.image && <img src={preview.image} alt={preview.title || "preview"} style={{ width: "100%", maxHeight: 160, objectFit: "cover" }} />}
                                          <div style={{ padding: 10 }}>
                                            <div style={{ fontWeight: 700, marginBottom: 4 }}>{preview.title || preview.url || url}</div>
                                            {!!preview.description && <div style={{ fontSize: 12, opacity: 0.85 }}>{preview.description}</div>}
                                            <div style={{ marginTop: 6, fontSize: 11, opacity: 0.7 }}>{preview.site_name || preview.url || url}</div>
                                          </div>
                                        </div>
                                      </a>
                                    );
                                  })}
                                  {imageAttachments.length > 0 ? (
                                    <div
                                      style={{
                                        marginTop: msg.content ? 10 : 0,
                                        display: "grid",
                                        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                                        gap: 8,
                                      }}
                                    >
                                      {imageAttachments.map((att, imageIndex) => {
                                        const imageUrls = imageAttachments.map((item) => item.url).filter(Boolean);
                                        return (
                                          <img
                                            key={att.id || `${msg.id}-img-${imageIndex}`}
                                            src={att.url}
                                            alt={att.title || "chat-image"}
                                            style={{ width: "100%", aspectRatio: "1 / 1", objectFit: "cover", borderRadius: 10, cursor: "zoom-in", border: "1px solid rgba(148,163,184,0.28)" }}
                                            onClick={() => openImagePreview(imageUrls, imageIndex)}
                                          />
                                        );
                                      })}
                                    </div>
                                  ) : null}

                                  {nonImageAttachments.map((att) => (
                                    <div key={att.id}>
                                      {renderAttachment(att, {
                                        onPreviewImage: (url) => openImagePreview([url], 0),
                                        onOpenOrderDetail,
                                      })}
                                    </div>
                                  ))}
                                </div>
                                {showSeen && (
                                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 4, textAlign: "right" }}>
                                    Đã xem
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </React.Fragment>
                      );
                        })}
                      </>
                    )}
                    <div ref={messageEndRef} />
                  </div>
                </div>

                <div style={{ padding: 16, borderTop: "1px solid #eef2f7", background: "rgba(255,255,255,0.95)" }}>
                  {!!typingText && (
                    <div style={{ marginBottom: 8, fontSize: 12, color: "#64748b" }}>{typingText}</div>
                  )}
                  {attachments.length > 0 && (
                    <div style={{ display: "flex", gap: 8, overflowX: "auto", marginBottom: 10, paddingBottom: 2 }}>
                      {attachments.map((att, idx) => (
                        <div key={`${att.type}-${idx}`} style={{ minWidth: 200, maxWidth: 240, borderRadius: 14, background: "#fff", border: "1px solid #e2e8f0", padding: 10 }}>
                          {renderAttachment(att, {
                            onPreviewImage: (url) => openImagePreview([url], 0),
                            onOpenOrderDetail,
                          })}
                          <div style={{ marginTop: 6, textAlign: "right" }}>
                            <Tooltip title="Xóa">
                              <Button size="small" type="text" danger onClick={() => onRemoveAttachment(idx)} icon={<DeleteOutlined />} />
                            </Tooltip>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, overflowX: "auto", paddingBottom: 2 }}>
                    <Tooltip title={soundEnabled ? "Tắt âm" : "Bật âm"}>
                      <Button size="small" onClick={toggleSoundEnabled} style={{ borderRadius: 999 }} icon={soundEnabled ? <AudioOutlined /> : <AudioMutedOutlined />} />
                    </Tooltip>
                    <Tooltip title="Gửi sản phẩm">
                      <Button size="small" onClick={() => openAttachmentPicker("product")} style={{ borderRadius: 999 }} icon={<ShoppingOutlined />} />
                    </Tooltip>
                    <Tooltip title="Gửi đơn hàng">
                      <Button size="small" onClick={() => openAttachmentPicker("invoice")} style={{ borderRadius: 999 }} icon={<FileTextOutlined />} />
                    </Tooltip>
                    <Tooltip title="Gửi giỏ hàng hiện tại">
                      <Button size="small" onClick={() => openAttachmentPicker("cart")} style={{ borderRadius: 999 }} icon={<ShoppingCartOutlined />} />
                    </Tooltip>
                    <Tooltip title="Gửi ảnh">
                      <div>
                        <ChatImagePicker
                          value={chatImagePickerValue}
                          onChange={onChangeChatImagePicker}
                          maxPhotos={10}
                        />
                      </div>
                    </Tooltip>
                  </div>

                  <div style={{ display: "flex", gap: 10, alignItems: "flex-end", background: "#fff", border: "1px solid #eef2f7", borderRadius: 24, padding: 12, boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)" }}>
                    <Input.TextArea
                      autoSize={{ minRows: 1, maxRows: 4 }}
                      placeholder="Nhập phản hồi cho khách hàng"
                      value={content}
                      variant="borderless"
                      onChange={(e) => setContent(e.target.value)}
                      onPressEnter={(e) => {
                        if (!e.shiftKey) {
                          e.preventDefault();
                          onSend();
                        }
                      }}
                    />
                    <Button type="primary" loading={sending} onClick={onSend} style={{ borderRadius: 999, height: 40, paddingInline: 18 }}>Gửi</Button>
                  </div>
                </div>

                <Modal
                  title={
                    attachmentPickerType === "product"
                      ? <Space><ShoppingOutlined /><span>Sản phẩm</span></Space>
                      : attachmentPickerType === "cart"
                        ? <Space><ShoppingCartOutlined /><span>Giỏ hàng hiện tại</span></Space>
                      : attachmentPickerType === "invoice"
                        ? <Space><FileTextOutlined /><span>Đơn hàng</span></Space>
                        : <Space><ShoppingCartOutlined /><span>Đơn hàng</span></Space>
                  }
                  open={attachmentPickerOpen}
                  onCancel={closeAttachmentPicker}
                  footer={null}
                  destroyOnClose
                >
                  <Space style={{ width: "100%", marginBottom: 12 }}>
                    <Input
                      placeholder={attachmentPickerType === "product" ? "Tìm sản phẩm" : attachmentPickerType === "cart" ? "Giỏ hàng hiện tại của khách" : "Tìm đơn hàng"}
                      value={attachmentPickerKeyword}
                      onChange={(e) => setAttachmentPickerKeyword(e.target.value)}
                      onPressEnter={() => {
                        if (!["product", "cart"].includes(attachmentPickerType)) {
                          loadAttachmentPickerData({ type: attachmentPickerType, info: attachmentPickerKeyword });
                        }
                      }}
                    />
                    {!['product', 'cart'].includes(attachmentPickerType) && (
                      <Tooltip title="Tìm">
                        <Button onClick={() => loadAttachmentPickerData({ type: attachmentPickerType, info: attachmentPickerKeyword })} icon={<SearchOutlined />} />
                      </Tooltip>
                    )}
                  </Space>
                  <List
                    loading={attachmentPickerLoading}
                    dataSource={attachmentPickerOptions}
                    locale={{ emptyText: <Empty description="Không có dữ liệu" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                    renderItem={(item) => (
                      <List.Item
                        onClick={() => onPickAttachment(item.raw)}
                        style={{ cursor: "pointer", borderRadius: 12, border: "1px solid #eef2f7", padding: 10, marginBottom: 8 }}
                      >
                        <List.Item.Meta
                          avatar={item.image ? <Avatar shape="square" size={44} src={item.image} /> : <Avatar>{item.title?.[0] || "#"}</Avatar>}
                          title={item.title}
                          description={attachmentPickerType === "product" ? item.subtitle || "" : (
                            <div style={{ fontSize: 12, color: "#64748b", display: "flex", flexDirection: "column", gap: 2 }}>
                              <div>{attachmentPickerType === 'cart' ? 'Cập nhật: ' : 'Ngày tạo: '}{item.createdAt || "-"}</div>
                              <div>Tổng tiền: {item.totalAmountText || "-"}</div>
                              <div>Số sản phẩm: {item.totalItem || 0}</div>
                              {attachmentPickerType !== 'cart' && <div style={{ wordBreak: "break-word" }}>Địa chỉ giao hàng: {item.shippingAddress || "-"}</div>}
                            </div>
                          )}
                        />
                      </List.Item>
                    )}
                  />
                </Modal>

                <Modal
                  open={!!previewImage}
                  footer={null}
                  onCancel={closeImagePreview}
                  centered
                  width="92vw"
                  styles={{ body: { padding: 8, textAlign: "center" } }}
                  destroyOnClose
                >
                  {!!previewImage && (
                    <div style={{ position: "relative" }}>
                      <img
                        src={previewImage}
                        alt="Xem ảnh"
                        style={{ width: "100%", maxHeight: "84vh", objectFit: "contain", borderRadius: 12 }}
                      />

                      {previewImages.length > 1 ? (
                        <>
                          <Button
                            type="primary"
                            shape="circle"
                            icon={<LeftCircleOutlined />}
                            onClick={showPrevImage}
                            style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)" }}
                          />
                          <Button
                            type="primary"
                            shape="circle"
                            icon={<RightCircleOutlined />}
                            onClick={showNextImage}
                            style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)" }}
                          />
                        </>
                      ) : null}
                    </div>
                  )}
                </Modal>
              </>
            ) : (
              <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(180deg, #f6f8fc 0%, #edf2f7 100%)" }}>
                <Empty description="Chọn một hội thoại để bắt đầu phản hồi" />
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ChatDesktop;
